/**
 * Iter121G — Add / Edit Case Note
 * Routes:
 *   /cases/:caseId/notes/new
 *   /cases/:caseId/notes/:noteId/edit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, StickyNote, Loader2, RefreshCw, Save } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';

const TOKEN_KEY = 'nexus_token';

export default function NoteFormPage() {
  const { caseId, noteId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const isEdit = Boolean(noteId);

  const [caseInfo, setCaseInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    category: 'General', subject: '', content: '',
    visibility: 'INTERNAL_ONLY', follow_up_required: false,
  });
  const setF = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (!caseId) return;
    fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setCaseInfo).catch(() => {});
  }, [caseId, token]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/case-notes/${noteId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
      const n = await res.json();
      setForm({
        category: n.category || 'General',
        subject: n.subject || '',
        content: n.content || '',
        visibility: n.visibility || 'INTERNAL_ONLY',
        follow_up_required: !!n.follow_up_required,
      });
    } catch (e) {
      toast({ title: 'Failed to load note', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [isEdit, noteId, token]);

  useEffect(() => { load(); }, [load]);

  const goBack = () => navigate(`/cases/${caseId}?tab=notes`);

  const submit = async () => {
    if (!form.content?.trim()) {
      toast({ title: 'Missing content', description: 'Note content is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const url = isEdit ? `${BACKEND_URL}/api/case-notes/${noteId}` : `${BACKEND_URL}/api/cases/${caseId}/notes`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(isEdit ? form : { ...form, case_id: caseId }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `Save failed (${res.status})`);
      toast({ title: isEdit ? 'Note updated' : 'Note added' });
      isEdit ? navigate(`/cases/${caseId}/notes/${noteId}/view`) : goBack();
    } catch (e) {
      toast({ title: isEdit ? 'Could not update note' : 'Could not add note', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading note…
      </div>
    );
  }

  return (
    <div className="space-y-5 nx-form-with-action-bar" data-testid="note-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button onClick={goBack} variant="outline" className="border-slate-600 !text-slate-200" data-testid="note-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Case
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <StickyNote className="w-6 h-6 text-amber-300" /> {isEdit ? 'Edit Case Note' : 'Add Case Note'}
          </h1>
          {caseInfo && <p className="text-xs text-slate-400 mt-0.5">
            {caseInfo.case_number} · <span className="text-slate-300">{caseInfo.title}</span>
          </p>}
        </div>
      </div>

      <div className="bg-[#131A2A] border border-slate-700 rounded-lg p-5 space-y-4 max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-300">Category</Label>
            <Select value={form.category} onValueChange={v => setF('category', v)}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="note-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['General','Investigation','Evidence','Client Communication','Legal','Financial','Follow-up'].map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Visibility</Label>
            <Select value={form.visibility} onValueChange={v => setF('visibility', v)}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="note-visibility-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INTERNAL_ONLY">Internal Only</SelectItem>
                <SelectItem value="ADMIN_ONLY">Admin Only</SelectItem>
                <SelectItem value="CLIENT_VIEWABLE">Client Viewable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-slate-300">Subject</Label>
          <Input value={form.subject} onChange={e => setF('subject', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" placeholder="Note subject" />
        </div>
        <div>
          <Label className="text-slate-300">Content *</Label>
          <Textarea value={form.content} onChange={e => setF('content', e.target.value)}
            className="bg-slate-800 border-slate-600 text-white" rows={8}
            placeholder="Note content..." data-testid="note-content-input" />
        </div>
      </div>

      <div className="nx-form-action-bar flex flex-wrap items-center justify-end gap-2" data-testid="note-action-bar">
        <Button onClick={goBack} variant="outline" className="border-slate-500 !text-slate-200" data-testid="note-cancel">
          Cancel
        </Button>
        <Button onClick={submit} disabled={saving || !form.content?.trim()}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold disabled:opacity-60"
          data-testid="submit-note-btn">
          {saving
            ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
            : (isEdit ? <><Save className="w-4 h-4 mr-1.5" /> Save Changes</> : 'Add Note')}
        </Button>
      </div>
    </div>
  );
}
